import React from 'react';
import clsx from 'clsx';
import { styled } from '@components-shared/src/stitches';
import type { ModalType } from '@context/modal';
import { ModalContext } from '@context/modal';
import { useMultipleNftsForRelease } from '@hooks/useMultipleNftsForRelease';

const STATUS_CIRCLE_SIZE = 24;

const COMMENT_STATUS_BAR_INFO: {
  [key: string]: {
    title: string;
    number: number;
  };
} = {
  BUY_NFT: {
    title: 'Collect NFT',
    number: 1,
  },
  SET_COMMENT: {
    title: 'Leave Comment',
    number: 2,
  },
  SHARE_TO_SOCIAL: {
    title: 'Done',
    number: 3,
  },
};

const getCurrentStep = ({
  activeModal,
}: {
  activeModal: ModalType | null;
  isMultiComment: boolean;
}) => {
  switch (activeModal) {
    case 'COMMENT_MODAL_V2':
      return 2;
    case 'SHARE_TO_SOCIAL':
      return 3;
    default:
      return 1;
  }
};

export function StatusBar({ releaseId, className }: { releaseId: string; className?: string }) {
  const { nftsOwnedByUser } = useMultipleNftsForRelease({ releaseId });
  const { activeModal } = ModalContext.useContainer();

  const isMultiComment = nftsOwnedByUser.length > 1;

  const statusBarInfo = COMMENT_STATUS_BAR_INFO;

  const getStatusBarSection = (key: string) => {
    const title = statusBarInfo[key]?.['title'];
    const stepNumber = statusBarInfo[key]?.['number'];

    if (!title || !stepNumber) return;

    const step = getCurrentStep({ activeModal, isMultiComment });

    const stepLength = Object.keys(statusBarInfo).length;

    const statusVariant =
      stepNumber === 1 || stepNumber < step
        ? 'COMPLETE'
        : stepNumber > step
        ? 'INCOMPLETE'
        : 'CURRENT';

    const connectorStatus =
      stepNumber === step || stepNumber > step
        ? 'INCOMPLETE_OR_CURRENT'
        : stepNumber + 1 === step
        ? 'COMPLETE_TO_CURRENT'
        : 'COMPLETE_TO_COMPLETE';

    // TODO: Find cleaner way to handle variants https://linear.app/sound/issue/ENG-6620/comment-modal-status-bar-cleanup
    return (
      <>
        <CircleContainer
          buyNft={key === 'BUY_NFT'}
          longText={key === 'SET_TIMESLOT' || key === 'SET_COMMENT' || key === 'SELECT_EDITION'}
          shareToSocial={key === 'SHARE_TO_SOCIAL'}
          fiveSteps={stepLength === 5}
        >
          <StatusCircle status={statusVariant}>{stepNumber}</StatusCircle>
          <SubText status={statusVariant}>{title}</SubText>
        </CircleContainer>
        <Connector status={connectorStatus} />
      </>
    );
  };

  return (
    <div className={clsx('relative flex h-[54px] w-full justify-center', className)}>
      {Object.keys(statusBarInfo).map((key: string) => (
        <React.Fragment key={key}>
          {key !== 'SHARE_TO_SOCIAL' ? (
            <div className="relative w-[calc((100%-24px)/2)]">{getStatusBarSection(key)}</div>
          ) : (
            getStatusBarSection(key)
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const Connector = styled('div', {
  height: 2,
  position: 'relative',
  bottom: 13,
  zIndex: '$above1',

  variants: {
    status: {
      COMPLETE_TO_CURRENT: {
        background: 'linear-gradient(to right, rgba(16, 185, 129, 1), rgba(23, 23, 23, 1))',
      },
      COMPLETE_TO_COMPLETE: {
        backgroundColor: '$brand500',
      },
      INCOMPLETE_OR_CURRENT: {
        backgroundColor: '$grey200',
      },
    },
  },
});

const CircleContainer = styled('div', {
  p: {
    fontSize: 11,
    paddingTop: 8,
    position: 'absolute',
    width: '100%',

    '@tablet': {
      fontSize: 14,
    },
  },
  variants: {
    buyNft: {
      true: {
        p: {
          width: '55%',
          '@tablet': {
            width: '100%',
          },
        },
      },
    },
    longText: {
      true: {
        p: {
          textAlign: 'center',
          '@media (min-width: 310px)': {
            width: '90%',
            right: '42%',
          },
          '@media (min-width: 505px)': {
            width: '100%',
            right: '40%',
          },
        },
      },
    },
    shareToSocial: {
      true: {
        position: 'relative',
        p: {
          display: 'flex',
          justifyContent: 'flex-end',
        },
      },
    },

    fiveSteps: {
      true: {},
    },
  },

  compoundVariants: [
    {
      longText: true,
      fiveSteps: true,
      css: {
        p: {
          '@media (min-width: 310px)': {
            width: '90%',
            right: '36%',
          },
          '@media (min-width: 505px)': {
            width: '100%',
            right: '37%',
          },
        },
      },
    },
  ],
});

const StatusCircle = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: '$above2',
  size: STATUS_CIRCLE_SIZE,
  borderRadius: 32,
  backgroundColor: '#fff',
  position: 'relative',
  base: 'S',
  color: 'white',
  fontWeight: '800',
  variants: {
    status: {
      CURRENT: {
        backgroundColor: 'black',
        color: 'white',
      },
      COMPLETE: {
        textAlign: 'center',
        backgroundColor: '$brand500',
        border: 'none',
      },
      INCOMPLETE: {
        backgroundColor: '$neutral100',
        base: 'S',
        color: '$neutral400',
        fontWeight: '800',
      },
    },
  },
});

const SubText = styled('p', {
  base: 'XS',
  lineHeight: '15px',
  fontStyle: 'semibold',
  color: '$neutral900',
  '@tablet': {
    base: 'S',
    width: '100%',
    textAlign: 'left',
  },
  variants: {
    status: {
      CURRENT: {},
      COMPLETE: {},
      INCOMPLETE: {
        color: '$neutral400',
        fontStyle: 'medium',
      },
    },
  },
});
